/**
 * @description Barra de navegação mobile para o modo interno
 * @module AvatarMenu
 * @since Fevereiro 2024
 */

import React from "react";
import {
  Avatar,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Quiz, Logout, Article } from "@mui/icons-material";
import { useLoggedUserContext } from "@/contexts/LoggedUserContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { IFeatures } from "@/utils/interfaces";
import { TermOfUseDialog } from "@/pages/TermsOfUse/TermOfUseDialog";
import { sendWebViewMessage, MessageTypes } from "@/utils/webViewBridge";

const AvatarMenu: React.FC<{
  handleMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  handleMenuClose: () => void;
  anchorEl: null | HTMLElement;
}> = ({ handleMenuOpen, handleMenuClose, anchorEl }) => {
  const { user, access, isOpenTermDialog, setIsOpenTermDialog } =
    useSessionContext();

  const { avatarMenuItens } = useLoggedUserContext();

  const suporteLink =
    access.type === "professional"
      ? "https://projectcore.atlassian.net/servicedesk/customer/portal/11"
      : "https://projectcore.atlassian.net/servicedesk/customer/portal/12";

  const navigate = useNavigate();

  const getUserInitials = () => {
    if (!user?.name) return "U";
    const names = user.name.split(" ");

    return names[0][0].toUpperCase();
  };

  return (
    <>
      <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
        <Avatar sx={{ bgcolor: "neutral100.main", color: "primary.main" }}>
          {getUserInitials()}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        sx={{ marginTop: "8px" }}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem>
          <Typography variant="body2">{user?.name || "Usuário"}</Typography>
        </MenuItem>
        <Divider />
        {avatarMenuItens?.map((item: IFeatures, id: number) => (
          <MenuItem
            key={`layout-private-user-options-menu-${id}`}
            onClick={() => {
              if (item.path) {
                navigate(item.path);
              }
            }}
            sx={{ "&:hover": { backgroundColor: "primary200.main" } }}
          >
            <ListItemIcon>
              {item.icon &&
                React.createElement(item.icon, {
                  sx: { fontSize: "24px", color: "primary1000.main" },
                })}
            </ListItemIcon>
            <ListItemText sx={{ color: "neutral1000.main" }}>
              {item.text}
            </ListItemText>
          </MenuItem>
        ))}
        <Stack
          style={{ textDecoration: "none", cursor: "pointer" }}
          onClick={() => window.open(suporteLink, "_blank")}
        >
          <MenuItem sx={{ "&:hover": { backgroundColor: "primary200.main" } }}>
            <ListItemIcon>
              <Quiz sx={{ fontSize: "24px", color: "primary1000.main" }} />
            </ListItemIcon>
            <ListItemText sx={{ color: "neutral1000.main" }}>
              Feedback e suporte
            </ListItemText>
          </MenuItem>
        </Stack>
        <Stack
          style={{ textDecoration: "none", cursor: "pointer" }}
          onClick={() => setIsOpenTermDialog(true)}
        >
          <MenuItem sx={{ "&:hover": { backgroundColor: "primary200.main" } }}>
            <ListItemIcon>
              <Article sx={{ fontSize: "24px", color: "primary1000.main" }} />
            </ListItemIcon>
            <ListItemText sx={{ color: "neutral1000.main" }}>
              Termos de uso
            </ListItemText>
          </MenuItem>
        </Stack>
        <MenuItem
          onClick={() => {
            localStorage.clear();
            // Notifica o app nativo sobre o logout
            sendWebViewMessage({
              type: MessageTypes.LOGOUT
            });
            navigate("/signin");
          }}
          sx={{ "&:hover": { backgroundColor: "primary200.main" } }}
        >
          <ListItemIcon>
            <Logout sx={{ fontSize: "24px", color: "primary1000.main" }} />
          </ListItemIcon>
          <ListItemText sx={{ color: "neutral1000.main" }}>Sair</ListItemText>
        </MenuItem>
      </Menu>
      {isOpenTermDialog && <TermOfUseDialog isOpen={isOpenTermDialog} />}
    </>
  );
};

export { AvatarMenu };
